import { EmptyState } from '@orion/ui';
import { useMeetingsQuery } from './api.js';

/** A lead's meetings rarely span more than a couple of years either side of
 * "now" — wide enough to cover any realistic booking without needing a
 * separate unbounded-range endpoint shape just for this one view. */
function wideRange(): { from: string; to: string } {
  const now = new Date();
  return {
    from: new Date(now.getFullYear() - 5, now.getMonth(), 1).toISOString(),
    to: new Date(now.getFullYear() + 2, now.getMonth(), 1).toISOString(),
  };
}

export function LeadMeetingsPanel({ leadId }: { leadId: string }) {
  const { data: meetings, isLoading } = useMeetingsQuery({ ...wideRange(), leadId });

  if (isLoading) return <p className="text-sm text-text-muted">Loading…</p>;
  if (!meetings || meetings.length === 0) {
    return <EmptyState title="No meetings scheduled" description="Bookings made through this rep's connected Calendly page will show up here." />;
  }

  return (
    <ul className="flex flex-col gap-2.5">
      {meetings.map((m) => (
        <li key={m.id} className="flex flex-col gap-1 rounded-control border border-border p-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-ink">{m.title}</span>
            {m.status === 'canceled' ? (
              <span className="rounded-[6px] border border-border px-2 text-xs font-semibold text-danger">Canceled</span>
            ) : (
              <span className="rounded-[6px] bg-amber-soft px-2 text-xs font-semibold text-amber-text">Scheduled</span>
            )}
          </div>
          <span className="text-sm text-text-secondary">
            {new Date(m.startTime).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}
            {m.endTime && ` – ${new Date(m.endTime).toLocaleTimeString('en-US', { timeStyle: 'short' })}`}
          </span>
          {m.location && <span className="text-xs text-text-muted">{m.location}</span>}
          <span className="text-xs text-text-muted">Booked by {m.inviteeName ?? m.inviteeEmail}</span>
        </li>
      ))}
    </ul>
  );
}
